import React, { useCallback, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import { useLivePoll, hasOpenRow } from "../lib/livePoll";

// 2026-10-13 — chat: "For new user, they don't see Live Orders menu at all. At
// least user should see Live orders and list of trades". A linked (family) Kite
// account trades through the mirror (backend kite_mirror.py); this lists that
// user's OWN trades — one row per contract per day — from
// GET /api/live-orders/my-trades.

const fmt = (v, d = 2) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d });

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const STATUS_COLOR = {
  OPEN: "var(--bull)", CLOSED: "var(--text-muted)", PENDING: "var(--text-muted)",
  "NOT FILLED": "var(--text-muted-2)", REJECTED: "var(--bear)", SKIPPED: "var(--text-muted-2)",
};

const STATUS_HELP = {
  SKIPPED: "Not sent to your account",
  "NOT FILLED": "Order sent but not filled — cancelled",
  PENDING: "Order waiting at the broker",
};

export const MyLiveTrades = ({ market = null, title = "My live trades" }) => {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/live-orders/my-trades", { params: { days, ...(market ? { market } : {}) } });
      setD(r.data);
    } catch {
      setD({ trades: [], summary: {} });
    } finally {
      setLoading(false);
    }
  }, [days, market]);

  const s = (d && d.summary) || {};
  const trades = (d && d.trades) || [];
  useLivePoll(load, hasOpenRow(trades), [load]);
  const pnl = Number(s.realised_pnl_rs || 0);

  return (
    <section className="border-grid surface" data-testid="my-live-trades">
      <div className="px-4 py-3 border-b border-grid flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-mono-data text-muted-2">{title}</div>
          <div className="text-[10px] text-muted-3 font-mono-data mt-0.5">
            Real orders placed in your Zerodha account by the autopilot (same moment as the main account).
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-transparent border border-grid text-[11px] font-mono-data px-2 py-1"
            data-testid="my-live-trades-days"
          >
            {[1, 7, 14, 30].map((n) => (
              <option key={n} value={n} className="bg-black">{n === 1 ? "Today" : `Last ${n} days`}</option>
            ))}
          </select>
          <button onClick={load} className="border border-grid px-2 py-1 text-muted-2" title="Refresh">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] font-mono-data border-b border-grid">
        <div><div className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">Live trades</div>{s.live_trades ?? 0}</div>
        <div><div className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">Closed · won</div>{s.closed ?? 0} · {s.won ?? 0}</div>
        <div><div className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">Open now</div>{s.open ?? 0}</div>
        <div>
          <div className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">Realised P&amp;L</div>
          <span style={{ color: pnl > 0 ? "var(--bull)" : pnl < 0 ? "var(--bear)" : undefined }}>
            {pnl >= 0 ? "+" : "−"}₹{fmt(Math.abs(pnl), 0)}
          </span>
          <span className="text-muted-3"> (before charges)</span>
        </div>
      </div>

      {s.dry_run > 0 && (
        <div className="px-4 py-2 text-[10px] font-mono-data text-muted-2 border-b border-grid">
          {s.dry_run} row{s.dry_run === 1 ? " is a" : "s are"} DRY RUN — the admin has not switched on real orders
          for your account yet; prices shown are what the main account got.
        </div>
      )}

      {trades.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-muted-2 font-mono-data" data-testid="my-live-trades-empty">
          {loading ? "Loading…" : "No live trades in this period."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono-data">
            <thead className="text-muted-3 text-[9px] uppercase tracking-[0.15em]">
              <tr className="border-b border-grid">
                {["Entered", "Strategy", "Contract", "Qty", "Entry", "Exit", "P&L", "Status"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trades.map((t, i) => (
                <tr key={`${t.day}-${t.tradingsymbol}-${t.mode}-${i}`} className="border-b border-grid/40">
                  <td className="px-3 py-1.5 whitespace-nowrap">{fmtTime(t.entered_at)}</td>
                  <td className="px-3 py-1.5">
                    {t.strategy}
                    {t.mode === "dry_run" && <span className="ml-1 text-[9px] border border-grid px-1 text-muted-2">DRY RUN</span>}
                  </td>
                  <td className="px-3 py-1.5 font-semibold">{t.tradingsymbol}</td>
                  <td className="px-3 py-1.5">{t.qty}{t.qty_unit === "lots" ? " lot" + (t.qty === 1 ? "" : "s") : ""}</td>
                  <td className="px-3 py-1.5">{t.entry_price == null ? "—" : `₹${fmt(t.entry_price)}`}</td>
                  <td className="px-3 py-1.5">{t.exit_price == null ? "—" : `₹${fmt(t.exit_price)}`}</td>
                  <td className="px-3 py-1.5" style={{ color: (t.pnl_rs ?? t.pnl_pct ?? 0) > 0 ? "var(--bull)" : (t.pnl_rs ?? t.pnl_pct ?? 0) < 0 ? "var(--bear)" : undefined }}>
                    {t.pnl_rs != null ? `${t.pnl_rs >= 0 ? "+" : "−"}₹${fmt(Math.abs(t.pnl_rs), 0)}` : ""}
                    {t.pnl_pct != null ? ` (${t.pnl_pct >= 0 ? "+" : ""}${t.pnl_pct}%)` : (t.pnl_rs == null ? "—" : "")}
                  </td>
                  <td className="px-3 py-1.5" title={t.reason || STATUS_HELP[t.status] || ""}>
                    <span style={{ color: STATUS_COLOR[t.status] }}>{t.status}</span>
                    {t.reason && (t.status === "SKIPPED" || t.status === "REJECTED") && (
                      <div className="text-[9px] text-muted-3 max-w-[220px] truncate">{t.reason}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default MyLiveTrades;
